import { useEffect } from 'react'
import { useAuthStore } from '../stores/useAuthStore.js'
import { useThemeStore } from '../stores/useThemeStore.js'

interface AuthMessage {
  type: 'chatwoot:auth'
  accessToken: string
  client: string
  uid: string
}

interface ThemeMessage {
  type: 'chatwoot:theme'
  theme: 'light' | 'dark'
}

type PostMessageData = AuthMessage | ThemeMessage

function isPostMessageData(data: unknown): data is PostMessageData {
  return typeof data === 'object' && data !== null && 'type' in data
}

/** Allowed origin for postMessage — set via VITE_CHATWOOT_ORIGIN env var */
const ALLOWED_ORIGIN = import.meta.env.VITE_CHATWOOT_ORIGIN as string | undefined

export function usePostMessage() {
  const setTokens = useAuthStore((s) => s.setTokens)
  const setTheme = useThemeStore((s) => s.setTheme)

  useEffect(() => {
    function handleMessage(event: MessageEvent) {
      // Validate origin if configured
      if (ALLOWED_ORIGIN && event.origin !== ALLOWED_ORIGIN) return

      const data = event.data
      if (!isPostMessageData(data)) return

      switch (data.type) {
        case 'chatwoot:auth':
          setTokens({
            accessToken: data.accessToken,
            client: data.client,
            uid: data.uid,
          })
          break
        case 'chatwoot:theme':
          setTheme(data.theme)
          break
      }
    }

    window.addEventListener('message', handleMessage)

    // Tell the parent (dashboard script) we're ready to receive auth
    if (window.parent && window.parent !== window) {
      window.parent.postMessage({ type: 'chatwoot:ready' }, '*')
    }

    return () => window.removeEventListener('message', handleMessage)
  }, [setTokens, setTheme])
}
